/**
 * Formatting helpers — all locale-aware where applicable.
 */

/**
 * Format fils as BHD with the conventional 3-decimal Bahraini format.
 * Example: 3718 → "BHD 3.718"  /  25000 → "BHD 25.000"
 */
export function formatBHD(fils: number, opts: { showCode?: boolean } = {}): string {
  const showCode = opts.showCode ?? true;
  const bhd = (fils / 1000).toFixed(3);
  return showCode ? `BHD ${bhd}` : bhd;
}

/**
 * Format a Bahrain phone number into a readable shape.
 * Input: "+97333000099"  →  "+973 3300 0099"
 */
export function formatPhone(phone: string): string {
  if (!phone) return '';
  const m = phone.match(/^\+(\d{3})(\d{4})(\d{4})$/);
  if (!m) return phone;
  return `+${m[1]} ${m[2]} ${m[3]}`;
}

/**
 * Format a bilingual { en, ar } object for the current locale.
 */
export function bilingual(value: { en?: string; ar?: string } | string | undefined, locale: 'en' | 'ar'): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value[locale] || value.en || value.ar || '';
}

/**
 * Pick `value.en` or `value.ar` based on `<html lang>`. Convenience for components.
 */
export function pickLocale<T extends { en?: string; ar?: string }>(value: T | undefined): string {
  if (!value) return '';
  const lang = document.documentElement.lang as 'en' | 'ar';
  return bilingual(value, lang || 'en');
}

/**
 * Format an ISO date as a short readable string in the current locale.
 */
export function formatDate(iso: string | Date | null | undefined, locale: 'en' | 'ar' = 'en'): string {
  if (!iso) return '';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-BH' : 'en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  }).format(d);
}

export function formatDateTime(iso: string | Date | null | undefined, locale: 'en' | 'ar' = 'en'): string {
  if (!iso) return '';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-BH' : 'en-GB', {
    day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
  }).format(d);
}

/**
 * Format Bahrain phone for input — the canonical leading "+973 " then digits.
 */
export function normalizeBahrainPhone(input: string): string {
  // Strip everything but digits, then prepend +973
  const digits = input.replace(/\D/g, '').replace(/^973/, '');
  return `+973${digits}`;
}
